<template>
	<page-container :page="page">
		<heading-image
			v-if="page.headingImage"
			:heading-image="{
				image: page.headingImage,
				imageWebp: page.headingImageWebp,
				imagePortrait: page.headingImagePortrait,
				imagePortraitWebp: page.headingImagePortraitWebp,
			}"
		/>
		<breadcrumbs :breadcrumbs="page.breadcrumbs" :class="page.headingImage ? '' : 'no-slider'" />

		<main-content>
			<template #content>
				<div class="columns column6">
					<h1>{{ page.title || page.header }}</h1>
					<p>
						<a
							v-if="page.bookingID"
							:href="page.bookingID"
							class="button button-cta"
							target="_blank"
							aria-label="Reserve now"
						>
							<span>{{ $t('bookThisPackage') }}</span>
						</a>
						<book-button v-else class="button button-cta" aria-label="Reserve now">
							<span>{{ $t('bookThisPackage') }}</span>
						</book-button>
					</p>
				</div>
				<div class="columns column6">
					<div v-parse-links v-html="page.content" />
				</div>
			</template>
		</main-content>

		<section class="main-content background-light align-left">
			<div class="row">
				<div class="columns column6">
					<div v-parse-links v-html="page.details" />
					<picture>
						<source v-if="page.sideImageWebp" :srcset="page.sideImageWebp" type="image/webp" />
						<source :srcset="page.sideImage" />
						<img :src="page.sideImage" class="extra-image" loading="lazy" :alt="page.sideImageAlt" />
					</picture>
				</div>
				<div class="columns column5 right package-icons">
					<div v-for="item in page.extras" :key="item.id" class="package-icon">
						<div v-if="item.icon == 'breathe'" class="icon icon-breathe" />
						<div v-else-if="item.icon == 'tickets'" class="icon icon-tickets" />
						<div v-else-if="item.icon == 'breakfast'" class="icon icon-breakfast" />
						<div v-else-if="item.icon == 'welcomebasket'" class="icon icon-welcomebasket" />
						<div v-else-if="item.icon == 'bicycle'" class="icon icon-bicycle" />
						<div v-else-if="item.icon == 'clock'" class="icon icon-clock" />
						<div v-else-if="item.icon == 'ship'" class="icon icon-ship" />
						<div v-else-if="item.icon == 'heart'" class="icon icon-heart" />
						<div v-else-if="item.icon == 'wine-bottle'" class="icon icon-winebottle" />
						<div v-else class="icon-svg-big">
							<font-awesome-icon :icon="`fal fa-${item.icon}`" size="1x" />
						</div>
						<span>{{ item.content }}</span>
					</div>
				</div>
			</div>
		</section>

		<pre-footer v-if="page && page.footerSection.length" :footer-section="page.footerSection[0]">
			<picture>
				<source v-if="footerSection.imageWebp" :srcset="footerSection.imageWebp" type="image/webp" />
				<source :srcset="footerSection.image" />
				<img :src="footerSection.image" class="bg-image" loading="lazy" :alt="footerSection.imageAlt" />
			</picture>
		</pre-footer>

		<section class="package-prefooter">
			<div class="row align-center">
				<div class="columns column12">
					<div v-parse-links v-html="page.bannerContent" />
					<a
						v-if="page.bookingID"
						:href="page.bookingID"
						class="button button-cta"
						target="_blank"
						aria-label="Reserve now"
					>
						<span>{{ $t('bookThisPackage') }}</span>
					</a>
					<book-button v-else class="button button-cta" aria-label="Reserve now">
						<span>{{ $t('bookThisPackage') }}</span>
					</book-button>
				</div>
			</div>
		</section>
	</page-container>
</template>

<script setup>
const { page, fetchPage } = usePage();

await fetchPage();
</script>

<style lang="scss">
.package-prefooter {
	padding: 50px 0;
	background: $contentblock-background-color;
	color: #fff;
}

.package-prefooter h2 {
	color: #fff;
}

.extra-image {
	width: 100%;
	max-width: 600px;
	margin: 20px auto 0;
}

.package-icons {
	display: flex;
	flex-wrap: wrap;
	justify-content: space-between;
	align-items: stretch;
}

.package-icon {
	width: 46%;
	background: #fff;
	padding: 40px 30px;
	display: flex;
	flex-wrap: wrap;
	justify-content: center;
	text-align: center;
	margin: 10px auto;

	.icon {
		width: 80px;
		height: 80px;
		margin: 0 0 20px;

		&.icon-breathe {
			background: url('~/assets/images/icons/icon-breathe.svg') no-repeat center center;
			background-size: 80px;
		}

		&.icon-tickets {
			background: url('~/assets/images/icons/icon-tickets.svg') no-repeat center center;
			background-size: 80px;
		}

		&.icon-breakfast {
			background: url('~/assets/images/icons/icon-breakfast.svg') no-repeat center center;
			background-size: 80px;
		}

		&.icon-welcomebasket {
			background: url('~/assets/images/icons/icon-welcome-basket.svg') no-repeat center center;
			background-size: 80px;
		}

		&.icon-clock {
			background: url('~/assets/images/icons/icon-clock.svg') no-repeat center center;
			background-size: 80px;
		}

		&.icon-bicycle {
			background: url('~/assets/images/icons/icon-bicycle.svg') no-repeat center center;
			background-size: 80px;
		}

		&.icon-ship {
			background: url('~/assets/images/icons/icon-ship.png') no-repeat center center;
			background-size: 80px;
		}

		&.icon-winebottle {
			background: url('~/assets/images/icons/icon-wine-bottle.png') no-repeat center center;
			background-size: 80px;
		}

		&.icon-heart {
			background: url('~/assets/images/icons/icon-heart.png') no-repeat center center;
			background-size: 80px;
		}
	}
}

.package-icon i,
.package-icon svg {
	color: #bf702a;
	font-size: 66px;
	margin: 0 auto 40px;
}

.package-icon span {
	width: 100%;
	font-size: 18px;
}

@media (max-width: 920px) {
	.package-icons {
		width: 100%;
	}
}

@media (max-width: 680px) {
	.package-icon {
		padding: 30px 20px;
		width: 48%;
	}

	.package-icon i,
	.package-icon svg {
		font-size: 58px;
	}

	.package-icon span {
		font-size: 16px;
	}
}
</style>
